<template>
  <ForecastsTabV2
    :slug="NEW_TECHNOLOGY.slug"
    :reports="reports"
    :available-reports="availableReports"
    :active-reports="activeReports"
    :is-loading="isLoading"
    :forecast-subscription="SUBSCRIPTION_FORECASTS_NEW_TECHNOLOGY"
    :show-download-all="showDownloadAll"
  />
</template>

<script lang="ts" setup>
import ForecastsTabV2 from '~/components/reports/ForecastsTabV2.vue'
import { useAuthorization } from '~/composables/useAuthorization'
import { useReports } from '~/composables/useReports'
import {
  NEW_TECHNOLOGY,
  SUBSCRIPTION_FORECASTS_NEW_TECHNOLOGY,
} from '~/constants/services/constants'
import { MARKET_SLUG, MARKET_TYPE } from '~/src/constants/markets'
import type { TMarketSlug } from '~/src/types/markets'
import type { TReportCard } from '~/src/types/reports'
import { filterReports } from '~/src/utils/reports/filter'
import { setReportImages } from '~/src/utils/reports/image'
import {
  sortByTitleReport,
  sortedByForecastsRecycling,
} from '~/src/utils/reports/sort'
import { useAuthStore } from '~/stores/auth'

const MARKET_TYPES = [MARKET_TYPE.FORECASTS]
const SHOW_TOTAL = true

const { getReportAndModelData } = useReports()
const { hasAuthorized } = useAuthorization()
const authStore = useAuthStore()
const isAuthenticated = computed(() => authStore.authenticated)

const getData = async (slug: TMarketSlug) => {
  const data = await getReportAndModelData(slug, MARKET_TYPES, SHOW_TOTAL, -1)

  const files = filterReports(data, 'forecast-video-intro')

  let reports = sortByTitleReport(files.noMatch)

  if (slug === MARKET_SLUG.RECYCLING) {
    reports = sortedByForecastsRecycling(reports)
  }
  reports = setReportImages(reports, slug)

  const availableReports = reports.filter((report) => !report.has_access)
  const activeReports = reports.filter((report) => report.has_access)

  return { reports, availableReports, activeReports }
}

const { data, status } = useAsyncData<{
  reports: TReportCard[]
  availableReports: TReportCard[]
  activeReports: TReportCard[]
  showDownloadAll: boolean
}>(
  `${NEW_TECHNOLOGY.slug}-data-reports-forecasts`,
  async () => {
    const [data, canShowDownloadAll] = await Promise.all([
      getData(NEW_TECHNOLOGY.slug),
      hasAuthorized(SUBSCRIPTION_FORECASTS_NEW_TECHNOLOGY),
    ])

    const showDownloadAll = canShowDownloadAll && data.reports.length > 0

    return {
      reports: data.reports,
      availableReports: data.availableReports,
      activeReports: data.activeReports,
      showDownloadAll,
    }
  },
  {
    watch: [isAuthenticated],
    default: () => ({
      reports: [],
      availableReports: [],
      activeReports: [],
      showDownloadAll: false,
    }),
  },
)

const isLoading = computed(
  () => status.value !== 'success' && status.value !== 'error',
)
const showDownloadAll = computed(() => data.value?.showDownloadAll)
const reports = computed(() => data.value?.reports)
const availableReports = computed(() => data.value?.availableReports)
const activeReports = computed(() => data.value?.activeReports)

definePageMeta({
  layout: 'services',
  isTab: true,
})

const titlePage = `${NEW_TECHNOLOGY.name} Data & Reports`
const { getSeoInfo } = useSeo()
const seo = getSeoInfo({
  title: titlePage,
  description:
    'Discover the next-generation market leaders and battery innovators reshaping the energy sector. Our reports and data create new supply chain opportunities.',
})

useHead({
  ...seo,
  title: `Benchmark Mineral Intelligence: ${titlePage}`,
})
</script>
